import type { PalettePage } from "./types";

/** Ids of the nested pages the palette can push. */
export const PAGE_IDS = {
  compare: "compare",
  pickEvent: "pick-event",
} as const;

export type PageId = (typeof PAGE_IDS)[keyof typeof PAGE_IDS];

/**
 * The nested pages, keyed by id.
 *
 * A page replaces the root list inside the same panel rather than opening a
 * second dialog: the breadcrumb tells you where you are and backspace on an
 * empty query gets you back, which is the pattern the Supabase palette uses for
 * its "Create..." drill-in.
 */
export const PAGES: Record<PageId, PalettePage> = {
  [PAGE_IDS.pickEvent]: {
    id: PAGE_IDS.pickEvent,
    title: "Which event?",
    placeholder: "Search your events...",
  },
  [PAGE_IDS.compare]: {
    id: PAGE_IDS.compare,
    title: "Compare",
    placeholder: "Select two or more proposals...",
  },
};

/**
 * Pops the top page off a stack.
 *
 * @param stack - The current page stack, root first.
 * @returns The stack with its last entry removed.
 */
export const popPage = (stack: PageId[]): PageId[] => stack.slice(0, -1);

/**
 * Whether a backspace should pop a page rather than edit the query.
 *
 * Only when there is a page to leave and no text to delete, so backspace keeps
 * its ordinary meaning the moment the user has typed anything.
 *
 * @param stack - The current page stack.
 * @param query - The current query text.
 * @returns true when backspace should pop.
 */
export const shouldPopOnBackspace = (stack: PageId[], query: string): boolean => stack.length > 0 && query.length === 0;
